import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Inbox, Loader2, LogOut, Mail, Phone, Save, Settings, Trash2, User, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { adminApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const { settings, refresh } = useSettings();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteForm, setSiteForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setSiteForm({
        company_name: settings.company_name || "",
        phone: settings.phone || "",
        email: settings.email || "",
        service_area: settings.service_area || "",
        hours: settings.hours || "",
        tagline: settings.tagline || "",
        about: settings.about || "",
      });
    }
  }, [settings]);

  const loadQuotes = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.listQuotes();
      setQuotes(data);
    } catch (e) {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadQuotes(); }, []);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const onSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.updateSettings(siteForm);
      await refresh();
      toast.success("Site info updated.");
    } catch (err) {
      toast.error("Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const onStatusChange = async (id, status) => {
    try {
      await adminApi.updateQuoteStatus(id, status);
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Status updated.");
    } catch (e) {
      toast.error("Failed to update status.");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    try {
      await adminApi.deleteQuote(id);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const newCount = quotes.filter((q) => q.status === "new").length;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-yellow-400 flex items-center justify-center">
              <Zap className="w-4.5 h-4.5 text-black" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-white font-display font-semibold text-sm tracking-tight leading-none">Admin</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-white/60 hover:text-yellow-400 text-sm"
              data-testid="admin-view-site"
            >
              <ExternalLink className="w-4 h-4" /> View Site
            </a>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-white/10 hover:border-yellow-400/50 text-white/80 hover:text-yellow-400 px-3.5 py-2 rounded-md text-sm transition-all"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="font-display font-black text-white text-3xl lg:text-5xl tracking-tighter leading-none">
            Dashboard
          </h1>
          <p className="text-white/50 mt-2">Manage quote requests and site information.</p>
        </div>

        {/* Stat cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <StatCard label="New Requests" value={newCount} accent="yellow" icon={Inbox} />
          <StatCard label="Total Requests" value={quotes.length} accent="blue" icon={Mail} />
          <StatCard label="Current Phone" value={settings?.phone || "—"} accent="white" icon={Phone} />
        </div>

        <Tabs defaultValue="quotes">
          <TabsList className="bg-[#0d0d0f] border border-white/10 p-1">
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-black text-white/70">
              Quote Requests
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-black text-white/70">
              Site Info
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="mt-6">
            <div className="bg-[#0d0d0f] border border-white/10 rounded-xl overflow-hidden">
              {loading ? (
                <div className="p-16 flex items-center justify-center text-white/50">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              ) : quotes.length === 0 ? (
                <div className="p-16 text-center">
                  <Inbox className="w-10 h-10 text-white/20 mx-auto mb-4" />
                  <div className="text-white/60">No quote requests yet.</div>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {quotes.map((q) => (
                    <QuoteRow key={q.id} quote={q} onStatusChange={onStatusChange} onDelete={onDelete} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <form
              onSubmit={onSaveSettings}
              data-testid="settings-form"
              className="bg-[#0d0d0f] border border-white/10 rounded-xl p-7 space-y-5 max-w-3xl"
            >
              <div className="flex items-center gap-2 pb-4 border-b border-white/10">
                <Settings className="w-5 h-5 text-yellow-400" />
                <h2 className="font-display font-semibold text-white text-lg tracking-tight">Company Information</h2>
              </div>

              {siteForm && (
                <>
                  <SettingField label="Company Name" testId="settings-company-field">
                    <Input
                      data-testid="settings-company-input"
                      value={siteForm.company_name}
                      onChange={(e) => setSiteForm({ ...siteForm, company_name: e.target.value })}
                      className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40"
                    />
                  </SettingField>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <SettingField label="Phone Number" testId="settings-phone-field">
                      <Input
                        data-testid="settings-phone-input"
                        value={siteForm.phone}
                        onChange={(e) => setSiteForm({ ...siteForm, phone: e.target.value })}
                        className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40"
                      />
                    </SettingField>
                    <SettingField label="Email" testId="settings-email-field">
                      <Input
                        data-testid="settings-email-input"
                        value={siteForm.email}
                        onChange={(e) => setSiteForm({ ...siteForm, email: e.target.value })}
                        className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40"
                      />
                    </SettingField>
                  </div>

                  <SettingField label="Service Area" testId="settings-area-field">
                    <Input
                      data-testid="settings-area-input"
                      value={siteForm.service_area}
                      onChange={(e) => setSiteForm({ ...siteForm, service_area: e.target.value })}
                      className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40"
                    />
                  </SettingField>

                  <SettingField label="Business Hours" testId="settings-hours-field">
                    <Input
                      data-testid="settings-hours-input"
                      value={siteForm.hours}
                      onChange={(e) => setSiteForm({ ...siteForm, hours: e.target.value })}
                      className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40"
                    />
                  </SettingField>

                  <SettingField label="Hero Tagline" testId="settings-tagline-field">
                    <Input
                      data-testid="settings-tagline-input"
                      value={siteForm.tagline}
                      onChange={(e) => setSiteForm({ ...siteForm, tagline: e.target.value })}
                      className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40"
                    />
                  </SettingField>

                  <SettingField label="About Us Text" testId="settings-about-field">
                    <Textarea
                      data-testid="settings-about-input"
                      value={siteForm.about}
                      onChange={(e) => setSiteForm({ ...siteForm, about: e.target.value })}
                      rows={4}
                      className="bg-black/40 border-white/10 text-white focus-visible:ring-yellow-400/40 resize-none"
                    />
                  </SettingField>

                  <button
                    type="submit"
                    disabled={saving}
                    data-testid="settings-save-button"
                    className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-70 text-black font-bold px-6 py-3 rounded-md transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? "Saving…" : "Save Changes"}
                  </button>
                </>
              )}
            </form>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent, icon: Icon }) {
  const accentClass = accent === "yellow" ? "text-yellow-400 bg-yellow-400/10 border-yellow-400/20"
    : accent === "blue" ? "text-blue-500 bg-blue-500/10 border-blue-500/20"
    : "text-white bg-white/5 border-white/10";
  return (
    <div className="bg-[#0d0d0f] border border-white/10 rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.22em] text-white/50 font-bold">{label}</div>
        <div className={`w-9 h-9 rounded-md border flex items-center justify-center ${accentClass}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-display font-black text-white text-3xl tracking-tight">{value}</div>
    </div>
  );
}

function SettingField({ label, testId, children }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <Label className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold">{label}</Label>
      {children}
    </div>
  );
}

function QuoteRow({ quote, onStatusChange, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const statusColor = quote.status === "new" ? "bg-yellow-400/15 text-yellow-400 border-yellow-400/30"
    : quote.status === "contacted" ? "bg-blue-500/15 text-blue-400 border-blue-500/30"
    : "bg-white/10 text-white/60 border-white/20";

  return (
    <div className="p-5 lg:p-6 hover:bg-white/[0.02] transition-colors" data-testid={`quote-row-${quote.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1.5 flex-wrap">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-white/40" />
              <span className="font-semibold text-white">{quote.name}</span>
            </div>
            <Badge className={`${statusColor} border`} variant="outline">{quote.status}</Badge>
            <span className="text-xs text-white/40">{new Date(quote.created_at).toLocaleString()}</span>
          </div>
          <div className="text-sm text-white/60 flex flex-wrap gap-x-5 gap-y-1">
            <span>{quote.email}</span>
            <span>{quote.phone}</span>
            <span className="text-yellow-400/80">{quote.service_type}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setExpanded(!expanded)}
            data-testid={`quote-expand-${quote.id}`}
            className="text-xs text-white/60 hover:text-yellow-400 px-3 py-2 border border-white/10 rounded-md"
          >
            {expanded ? "Hide" : "Details"}
          </button>
          <select
            value={quote.status}
            onChange={(e) => onStatusChange(quote.id, e.target.value)}
            data-testid={`quote-status-${quote.id}`}
            className="bg-black/40 border border-white/10 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
          >
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="scheduled">Scheduled</option>
            <option value="closed">Closed</option>
          </select>
          <button
            onClick={() => onDelete(quote.id)}
            data-testid={`quote-delete-${quote.id}`}
            className="text-red-400/80 hover:text-red-400 p-2 border border-white/10 rounded-md hover:border-red-500/30"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-5 pl-6 border-l-2 border-yellow-400/50 space-y-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-1">Address</div>
            <div className="text-white/80">{quote.address}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-1">Description</div>
            <div className="text-white/80 whitespace-pre-wrap">{quote.description}</div>
          </div>
          <a
            href={`tel:${quote.phone.replace(/[^0-9]/g, "")}`}
            className="inline-flex items-center gap-2 text-yellow-400 hover:underline text-sm"
          >
            <Phone className="w-3.5 h-3.5" /> Call {quote.name}
          </a>
        </div>
      )}
    </div>
  );
}
